import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

// Helper function to determine league status based on dates
const getLeagueStatus = (startDate: string, endDate: string): { status: 'upcoming' | 'active' | 'completed', color: string, bgColor: string } => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) {
    return { status: 'upcoming', color: 'text-yellow-300', bgColor: 'bg-yellow-500/20' };
  } else if (now >= start && now <= end) {
    return { status: 'active', color: 'text-green-300', bgColor: 'bg-green-500/20' };
  } else {
    return { status: 'completed', color: 'text-gray-300', bgColor: 'bg-gray-500/20' };
  }
};

interface Admin {
  id: number;
  name: string;
  email: string;
  created_at: string;
  super_admin: boolean;
  league_count: number;
}

interface League {
  id: number;
  name: string;
  description: string;
  venue: string;
  city: string;
  country: string;
  team_format: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
  total_squads: number;
  total_teams: number;
  total_bowlers: number;
  total_matches: number;
  total_side_events: number;
  staff_count: number;
}

interface Staff {
  id: number;
  staff_name: string;
  staff_email: string;
  role: string;
  added_at: string;
}

interface Participant {
  team_id: number;
  team_name: string;
  team_created: string;
  member_id: number;
  member_role: string;
  player_id: number;
  player_name: string;
  player_email: string;
  city: string;
  country: string;
  is_subscriber: boolean;
  subscription_expiration: string;
  payment_status: string;
  amount_paid: number;
  payment_date: string;
}

interface SideEvent {
  id: number;
  name: string;
  type: string;
  cost: number;
  description: string;
  applies_to: string;
  squad_name: string;
  squad_date: string;
  is_active: boolean;
  total_participants: number;
  paid_participants: number;
}

interface AwardsSettings {
  handicapSettings: {
    handicap_base: number;
    handicap_percentage: number;
    a_max: number;
    b_max: number;
    c_max: number;
    d_max: number;
    e_max: number;
    handicap_max: number;
    calculation_frequency: string;
    lock_handicap_after_weeks: number;
    lock_starting_hdcp_for_weeks: number;
  } | null;
  awards: Array<{
    id: number;
    name: string;
    type: string;
    amount: number;
    item_desc: string;
    per_date: boolean;
    overall: boolean;
    created_at: string;
  }>;
  leaderboardPayouts: Array<{
    id: number;
    name: string;
    description: string;
    placement: number;
    amount: number;
    type: string;
    item_desc: string;
  }>;
}

const MasterAdmins: React.FC = () => {
  const { t } = useTranslation();
  
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [adminLeagues, setAdminLeagues] = useState<League[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  
  // Search states
  const [adminSearch, setAdminSearch] = useState('');
  const [bowlerSearch, setBowlerSearch] = useState('');
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showSideEventsModal, setShowSideEventsModal] = useState(false);
  const [showAwardsModal, setShowAwardsModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);

  // Edit admin modal
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [editAdminForm, setEditAdminForm] = useState({ name: '', email: '' });
  const [editAdminTarget, setEditAdminTarget] = useState<Admin | null>(null);
  const [savingAdminEdit, setSavingAdminEdit] = useState(false);
  const [editAdminError, setEditAdminError] = useState<string | null>(null);

  // Set password modal for admin
  const [showAdminPwdModal, setShowAdminPwdModal] = useState(false);
  const [adminPwdTarget, setAdminPwdTarget] = useState<{ id: number; name: string; email: string } | null>(null);
  const [adminPwdValue, setAdminPwdValue] = useState('');
  const [adminPwdLoading, setAdminPwdLoading] = useState(false);
  const [adminPwdError, setAdminPwdError] = useState<string | null>(null);
  const [adminPwdSuccess, setAdminPwdSuccess] = useState(false);
  
  // Modal data
  const [staff, setStaff] = useState<Staff[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [sideEvents, setSideEvents] = useState<SideEvent[]>([]);
  const [awardsSettings, setAwardsSettings] = useState<AwardsSettings | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({});
  const [enteringLeague, setEnteringLeague] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoadingAdmins(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/admins/admins`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins);
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const fetchAdminLeagues = async (adminId: number) => {
    try {
      setLoadingLeagues(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/admins/admins/${adminId}/leagues`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminLeagues(data.leagues);
      }
    } catch (error) {
      console.error('Failed to fetch leagues:', error);
    } finally {
      setLoadingLeagues(false);
    }
  };

  const handleAdminClick = (admin: Admin) => {
    setSelectedAdmin(admin);
    setSelectedLeague(null);
    fetchAdminLeagues(admin.id);
  };

  const openEditAdminModal = (admin: Admin, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditAdminTarget(admin);
    setEditAdminForm({ name: admin.name, email: admin.email });
    setEditAdminError(null);
    setShowEditAdminModal(true);
  };

  const handleSaveAdminEdit = async () => {
    if (!editAdminTarget) return;
    setSavingAdminEdit(true);
    setEditAdminError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/admins/admins/${editAdminTarget.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editAdminForm)
      });
      if (response.ok) {
        const updated = { ...editAdminTarget, ...editAdminForm };
        setAdmins(prev => prev.map(a => a.id === updated.id ? updated : a));
        if (selectedAdmin?.id === updated.id) setSelectedAdmin(updated);
        setShowEditAdminModal(false);
      } else {
        const data = await response.json();
        setEditAdminError(data.error || 'Failed to update admin.');
      }
    } catch {
      setEditAdminError('Network error. Please try again.');
    } finally {
      setSavingAdminEdit(false);
    }
  };

  const openAdminPwdModal = (admin: { id: number; name: string; email: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    setAdminPwdTarget(admin);
    setAdminPwdValue('');
    setAdminPwdError(null);
    setAdminPwdSuccess(false);
    setShowAdminPwdModal(true);
  };

  const handleSetAdminPassword = async () => {
    if (!adminPwdTarget) return;
    if (!adminPwdValue || adminPwdValue.length < 6) {
      setAdminPwdError('Password must be at least 6 characters.');
      return;
    }
    setAdminPwdLoading(true);
    setAdminPwdError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/admins/admins/${adminPwdTarget.id}/set-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: adminPwdValue })
      });
      if (response.ok) {
        setAdminPwdSuccess(true);
      } else {
        const data = await response.json();
        setAdminPwdError(data.error || 'Failed to set password.');
      }
    } catch {
      setAdminPwdError('Network error. Please try again.');
    } finally {
      setAdminPwdLoading(false);
    }
  };

  const handleLeagueClick = (league: League) => {
    setSelectedLeague(league);
  };

  const fetchStaff = async (leagueId: number) => {
    const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
    const response = await fetch(`${apiUrl}/master/admins/leagues/${leagueId}/staff`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.ok) {
      const data = await response.json();
      setStaff(data.staff);
      setShowStaffModal(true);
    }
  };

  const fetchParticipants = async (leagueId: number) => {
    const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
    const response = await fetch(`${apiUrl}/master/admins/leagues/${leagueId}/participants`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.ok) {
      const data = await response.json();
      setParticipants(data.teams);
      setShowParticipantsModal(true);
    }
  };

  const fetchSideEvents = async (leagueId: number) => {
    const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
    const response = await fetch(`${apiUrl}/master/admins/leagues/${leagueId}/side-events`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.ok) {
      const data = await response.json();
      setSideEvents(data.sideEvents);
      setShowSideEventsModal(true);
    }
  };

  const fetchAwardsSettings = async (leagueId: number) => {
    const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
    const response = await fetch(`${apiUrl}/master/admins/leagues/${leagueId}/awards-settings`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.ok) {
      const data = await response.json();
      setAwardsSettings(data);
      setShowAwardsModal(true);
    }
  };

  const handleEnterLeague = async (league: League) => {
    setEnteringLeague(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/master/admins/leagues/${league.id}/enter`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.leagueType === 'bookie') {
          window.open(`/bookie/manage-league/${league.id}`, '_blank');
        } else {
          window.open(`/admin/leagues/${league.id}/manage`, '_blank');
        }
      }
    } catch (error) {
      console.error('Failed to enter league:', error);
    } finally {
      setEnteringLeague(false);
    }
  };

  const handleDeleteLeague = async () => {
    if (!selectedLeague) return;
    try {
      setDeleting(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/admins/leagues/${selectedLeague.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedLeague(null);
        if (selectedAdmin) {
          fetchAdminLeagues(selectedAdmin.id);
        }
      }
    } catch (error) {
      console.error('Failed to delete league:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Filter admins by search
  const filteredAdmins = admins.filter(admin => 
    admin.name.toLowerCase().includes(adminSearch.toLowerCase()) ||
    admin.email.toLowerCase().includes(adminSearch.toLowerCase()) ||
    admin.id.toString().includes(adminSearch)
  );

  // Filter bowlers/participants by search
  const filteredParticipants = participants.filter(p =>
    p.player_name.toLowerCase().includes(bowlerSearch.toLowerCase()) ||
    p.player_email.toLowerCase().includes(bowlerSearch.toLowerCase()) ||
    p.player_id.toString().includes(bowlerSearch)
  );

  return (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:gap-6 lg:h-[calc(100vh-300px)]">

      {/* Admin List */}
      <div className="lg:col-span-1 bg-white/10 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/20 overflow-y-auto">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
          {t('superadmin.admins.title')} ({admins.length})
        </h3>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name, email, or ID..."
            value={adminSearch}
            onChange={(e) => setAdminSearch(e.target.value)}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200/50 focus:outline-none focus:border-blue-400 text-sm"
          />
        </div>

        {loadingAdmins ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-400"></div>
          </div>
        ) : filteredAdmins.length === 0 ? (
          <p className="text-blue-200 text-center py-12">No admins found</p>
        ) : (
          <div className="space-y-2">
            {filteredAdmins.map((admin) => (
              <div
                key={admin.id}
                onClick={() => handleAdminClick(admin)}
                className={`w-full text-left p-3 rounded-lg transition-all cursor-pointer ${
                  selectedAdmin?.id === admin.id
                    ? 'bg-blue-600/50 border-2 border-blue-400'
                    : 'bg-white/5 hover:bg-white/10 border-2 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-white font-semibold text-sm truncate">{admin.name}</p>
                  {admin.super_admin ? <span className="text-yellow-400 shrink-0 ml-1">👑</span> : null}
                </div>
                <p className="text-blue-200 text-xs truncate">{admin.email}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-blue-300 text-xs">{admin.league_count} {t('superadmin.admins.leagues')}</p>
                  <p className="text-blue-400 text-xs">#{admin.id}</p>
                </div>
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={(e) => openEditAdminModal(admin, e)}
                    title="Edit admin"
                    className="text-xs px-2 py-0.5 bg-blue-500/20 hover:bg-blue-500/40 border border-blue-400/40 text-blue-300 rounded transition"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={(e) => openAdminPwdModal(admin, e)}
                    className="text-xs px-2 py-0.5 bg-yellow-500/20 hover:bg-yellow-500/40 border border-yellow-400/40 text-yellow-300 rounded transition"
                  >
                    🔑 Pwd
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Leagues + Inline Actions */}
      <div className="lg:col-span-2 bg-white/10 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/20 overflow-y-auto">
        {selectedAdmin ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl sm:text-2xl font-bold text-white truncate">
                {selectedAdmin.name}'s {t('superadmin.admins.leagues')}
              </h3>
              <button
                onClick={() => { setSelectedAdmin(null); setSelectedLeague(null); setAdminLeagues([]); }}
                className="text-white/40 hover:text-white transition shrink-0 ml-2 text-lg leading-none"
                title="Clear selection"
              >
                ✕
              </button>
            </div>

            {loadingLeagues ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-400"></div>
              </div>
            ) : adminLeagues.length === 0 ? (
              <p className="text-blue-200 text-center py-12">{t('superadmin.admins.noLeagues')}</p>
            ) : (
              <div className="space-y-3">
                {adminLeagues.map((league) => (
                  <div key={league.id}>
                    <button
                      onClick={() => handleLeagueClick(league)}
                      className={`w-full text-left p-4 rounded-lg transition-all ${
                        selectedLeague?.id === league.id
                          ? 'bg-green-600/50 border-2 border-green-400 rounded-b-none'
                          : 'bg-white/5 hover:bg-white/10 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-white font-bold truncate">{league.name}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs shrink-0 ml-2 ${getLeagueStatus(league.start_date, league.end_date).bgColor} ${getLeagueStatus(league.start_date, league.end_date).color}`}>
                          {getLeagueStatus(league.start_date, league.end_date).status === 'upcoming' && t('common.upcoming')}
                          {getLeagueStatus(league.start_date, league.end_date).status === 'active' && t('common.active')}
                          {getLeagueStatus(league.start_date, league.end_date).status === 'completed' && t('common.completed')}
                        </span>
                      </div>
                      <p className="text-blue-200 text-sm mb-1">{league.venue} · {league.city}, {league.country}</p>
                      <p className="text-blue-300 text-xs mb-2">{t('superadmin.admins.leagueId')}: {league.id}</p>
                      <div className="grid grid-cols-3 gap-1.5 text-xs">
                        <div className="bg-white/10 px-2 py-1 rounded">
                          <span className="text-blue-200">⚡ {league.total_squads} {t('superadmin.admins.squads')}</span>
                        </div>
                        <div className="bg-white/10 px-2 py-1 rounded">
                          <span className="text-blue-200">👥 {league.total_teams} {t('superadmin.admins.teams')}</span>
                        </div>
                        <div className="bg-white/10 px-2 py-1 rounded">
                          <span className="text-blue-200">🎳 {league.total_bowlers} {t('superadmin.admins.bowlers')}</span>
                        </div>
                        <div className="bg-white/10 px-2 py-1 rounded">
                          <span className="text-blue-200">🎯 {league.total_matches} {t('superadmin.admins.matches')}</span>
                        </div>
                        <div className="bg-white/10 px-2 py-1 rounded">
                          <span className="text-blue-200">🎪 {league.total_side_events} {t('superadmin.admins.sideEvents')}</span>
                        </div>
                        <div className="bg-white/10 px-2 py-1 rounded">
                          <span className="text-blue-200">👔 {league.staff_count} {t('superadmin.admins.staff')}</span>
                        </div>
                      </div>
                    </button>

                    {/* Action panel — expands inline below the selected league */}
                    {selectedLeague?.id === league.id && (
                      <div className="bg-green-900/30 border-2 border-green-400 border-t-0 rounded-b-lg p-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          <button
                            onClick={() => fetchStaff(league.id)}
                            className="flex items-center justify-between p-2.5 bg-blue-600/30 hover:bg-blue-600/50 rounded-lg text-white text-xs font-semibold transition"
                          >
                            <span>👔 {t('superadmin.admins.viewStaff')}</span>
                            <span className="opacity-60 shrink-0 ml-1">({league.staff_count})</span>
                          </button>
                          <button
                            onClick={() => fetchParticipants(league.id)}
                            className="flex items-center justify-between p-2.5 bg-green-600/30 hover:bg-green-600/50 rounded-lg text-white text-xs font-semibold transition"
                          >
                            <span>👥 {t('superadmin.admins.viewParticipants')}</span>
                            <span className="opacity-60 shrink-0 ml-1">({league.total_bowlers})</span>
                          </button>
                          <button
                            onClick={() => fetchSideEvents(league.id)}
                            className="flex items-center justify-between p-2.5 bg-purple-600/30 hover:bg-purple-600/50 rounded-lg text-white text-xs font-semibold transition"
                          >
                            <span>🎪 {t('superadmin.admins.viewSideEvents')}</span>
                            <span className="opacity-60 shrink-0 ml-1">({league.total_side_events})</span>
                          </button>
                          <button
                            onClick={() => fetchAwardsSettings(league.id)}
                            className="p-2.5 bg-yellow-600/30 hover:bg-yellow-600/50 rounded-lg text-white text-xs font-semibold transition text-left"
                          >
                            🏆 {t('superadmin.admins.viewAwardsSettings')}
                          </button>
                          <button
                            onClick={() => setShowDeleteModal(true)}
                            className="p-2.5 bg-red-600/30 hover:bg-red-600/50 rounded-lg text-white text-xs font-semibold transition text-left"
                          >
                            🗑️ {t('superadmin.admins.deleteLeague')}
                          </button>
                          <button
                            onClick={() => handleEnterLeague(league)}
                            disabled={enteringLeague}
                            className="p-2.5 bg-indigo-600/30 hover:bg-indigo-600/50 disabled:opacity-50 rounded-lg text-white text-xs font-semibold transition text-left"
                          >
                            {enteringLeague ? '⏳ Entering...' : '🚀 Enter League'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="text-6xl mb-4">👔</span>
            <p className="text-blue-200 text-lg">{t('superadmin.admins.selectAdmin')}</p>
            <p className="text-blue-300/60 text-sm mt-1">Pick an admin from the list to see their leagues</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedLeague && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-gradient-to-br from-red-900 to-red-700 rounded-xl p-8 max-w-2xl w-full mx-4 border-2 border-red-400" onClick={e => e.stopPropagation()}>
            <h2 className="text-3xl font-bold text-white mb-4">⚠️ {t('superadmin.admins.deleteWarning')}</h2>
            <p className="text-red-100 mb-6">{t('superadmin.admins.deleteConfirm', { name: selectedLeague.name })}</p>
            
            <div className="bg-black/30 p-4 rounded-lg mb-6">
              <p className="text-white font-semibold mb-2">{t('superadmin.admins.willDelete')}:</p>
              <ul className="text-red-100 space-y-1 text-sm">
                <li>• {selectedLeague.total_teams} {t('superadmin.admins.teams')}</li>
                <li>• {selectedLeague.total_bowlers} {t('superadmin.admins.bowlerRegistrations')}</li>
                <li>• {selectedLeague.total_squads} {t('superadmin.admins.squads')}</li>
                <li>• {selectedLeague.total_matches} {t('superadmin.admins.matches')}</li>
                <li>• {selectedLeague.total_side_events} {t('superadmin.admins.sideEvents')}</li>
              </ul>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg font-bold transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteLeague}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-bold transition disabled:opacity-50"
              >
                {deleting ? t('common.deleting') : t('superadmin.admins.permanentlyDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Modal */}
      {showStaffModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowStaffModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-4xl w-full mx-4 border border-white/20 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-3xl font-bold text-white mb-6">👔 {t('superadmin.admins.leagueStaff')}</h2>
            
            {staff.length === 0 ? (
              <p className="text-white text-center py-12">{t('superadmin.admins.noStaff')}</p>
            ) : (
              <div className="space-y-3">
                {staff.map((s) => (
                  <div key={s.id} className="bg-white/5 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white font-bold">{s.staff_name}</p>
                        <p className="text-blue-200 text-sm">{s.staff_email}</p>
                      </div>
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-semibold">
                        {s.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowStaffModal(false)}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipantsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowParticipantsModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-6xl w-full mx-4 border border-white/20 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-3xl font-bold text-white mb-6">👥 {t('superadmin.admins.allParticipants')}</h2>
            
            {/* Bowler Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search by name, email, or player ID..."
                value={bowlerSearch}
                onChange={(e) => setBowlerSearch(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200/50 focus:outline-none focus:border-blue-400"
              />
            </div>
            
            <div className="space-y-6">
              {Object.entries(
                filteredParticipants.reduce((acc, p) => {
                  if (!acc[p.team_id]) {
                    acc[p.team_id] = {
                      team_name: p.team_name,
                      members: []
                    };
                  }
                  acc[p.team_id].members.push(p);
                  return acc;
                }, {} as Record<number, { team_name: string; members: Participant[] }>)
              ).map(([teamId, data]) => (
                <div key={teamId} className="bg-white/5 p-4 rounded-lg">
                  <h3 className="text-white font-bold text-lg mb-3">{data.team_name}</h3>
                  <div className="space-y-2">
                    {data.members.map((member) => (
                      <div key={member.member_id} className="bg-white/5 p-3 rounded flex justify-between items-center">
                        <div>
                          <p className="text-white font-semibold">
                            {member.player_name} {member.is_subscriber ? '👑' : ''}
                          </p>
                          <p className="text-blue-200 text-sm">{member.player_email}</p>
                          <p className="text-blue-300 text-xs">Player ID: {member.player_id}</p>
                          <p className="text-blue-300 text-xs">{member.city}, {member.country}</p>
                          {member.is_subscriber && member.subscription_expiration ? (
                            <p className="text-yellow-300 text-xs">Expires: {new Date(member.subscription_expiration).toLocaleDateString()}</p>
                          ) : null}
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            member.payment_status === 'completed' ? 'bg-green-500/20 text-green-300' :
                            member.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                            'bg-red-500/20 text-red-300'
                          }`}>
                            {member.payment_status}
                          </span>
                          <p className="text-blue-300 text-xs mt-1">{member.member_role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowParticipantsModal(false)}
              className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Side Events Modal */}
      {showSideEventsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowSideEventsModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-5xl w-full mx-4 border border-white/20 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-3xl font-bold text-white mb-6">🎪 {t('superadmin.admins.sideEventsConfig')}</h2>
            
            {sideEvents.length === 0 ? (
              <p className="text-white text-center py-12">{t('superadmin.admins.noSideEvents')}</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(
                  sideEvents.reduce((acc, event) => {
                    if (!acc[event.type]) acc[event.type] = [];
                    acc[event.type].push(event);
                    return acc;
                  }, {} as Record<string, SideEvent[]>)
                ).map(([type, events]) => (
                  <div key={type} className="bg-white/5 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedTypes(prev => ({ ...prev, [type]: !prev[type] }))}
                      className="w-full p-4 flex justify-between items-center hover:bg-white/10 transition-colors"
                    >
                      <h3 className="text-white font-bold text-xl capitalize">{type.replace('_', ' ')}</h3>
                      <div className="flex items-center gap-3">
                        <span className="text-blue-300 text-sm">{events.length} event{events.length !== 1 ? 's' : ''}</span>
                        <span className="text-white text-2xl">{expandedTypes[type] ? '▼' : '▶'}</span>
                      </div>
                    </button>
                    
                    {expandedTypes[type] && (
                      <div className="p-4 pt-0 space-y-2">
                        {events.map((event) => (
                          <div key={event.id} className="bg-white/5 p-3 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="text-white font-semibold">{event.name}</h4>
                                {event.squad_name && (
                                  <p className="text-blue-300 text-xs mt-1">
                                    📅 {event.squad_name}
                                    {event.squad_date && ` - ${new Date(event.squad_date).toLocaleDateString()}`}
                                  </p>
                                )}
                              </div>
                              <span className="text-green-300 font-bold text-lg">${event.cost || 0}</span>
                            </div>
                            {event.description && (
                              <p className="text-blue-200 text-sm mb-2">{event.description}</p>
                            )}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <p className="text-blue-200">👥 {event.total_participants} {t('superadmin.admins.participants')}</p>
                              <p className="text-blue-200">✅ {event.paid_participants} {t('superadmin.admins.paid')}</p>
                              <p className="text-blue-200">🎯 {event.applies_to}</p>
                              <p className="text-blue-200">{event.is_active ? '✅ Active' : '⏸️ Inactive'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowSideEventsModal(false)}
              className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-bold transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Awards & Settings Modal */}
      {showAwardsModal && awardsSettings && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAwardsModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-4xl w-full mx-4 border border-white/20 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-3xl font-bold text-white mb-6">🏆 {t('superadmin.admins.awardsLeaderboard')}</h2>
            
            {/* Handicap Settings */}
            {awardsSettings.handicapSettings && (
              <div className="bg-white/5 p-4 rounded-lg mb-6">
                <h3 className="text-white font-bold text-lg mb-3">{t('superadmin.admins.handicapSettings')}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <p className="text-blue-200">{t('superadmin.admins.handicapBase')}: {awardsSettings.handicapSettings.handicap_base}</p>
                  <p className="text-blue-200">{t('superadmin.admins.handicapPercentage')}: {awardsSettings.handicapSettings.handicap_percentage}%</p>
                  <p className="text-blue-200">{t('superadmin.admins.handicapMax')}: {awardsSettings.handicapSettings.handicap_max}</p>
                  <p className="text-blue-200">{t('superadmin.admins.frequency')}: {awardsSettings.handicapSettings.calculation_frequency}</p>
                  <p className="text-blue-200">{t('superadmin.admins.aMax')}: {awardsSettings.handicapSettings.a_max}</p>
                  <p className="text-blue-200">{t('superadmin.admins.bMax')}: {awardsSettings.handicapSettings.b_max}</p>
                  <p className="text-blue-200">{t('superadmin.admins.cMax')}: {awardsSettings.handicapSettings.c_max}</p>
                  <p className="text-blue-200">{t('superadmin.admins.dMax')}: {awardsSettings.handicapSettings.d_max}</p>
                  <p className="text-blue-200">{t('superadmin.admins.eMax')}: {awardsSettings.handicapSettings.e_max}</p>
                </div>
              </div>
            )}

            {/* Awards */}
            {awardsSettings.awards.length > 0 && (
              <div className="bg-white/5 p-4 rounded-lg mb-6">
                <h3 className="text-white font-bold text-lg mb-3">{t('superadmin.admins.awards')}</h3>
                <div className="space-y-2">
                  {awardsSettings.awards.map((award, idx) => (
                    <div key={idx} className="bg-white/5 p-3 rounded flex justify-between">
                      <div>
                        <p className="text-white font-semibold">{award.name}</p>
                        <p className="text-blue-300 text-sm">{award.type}</p>
                        {award.item_desc && <p className="text-blue-200 text-xs">{award.item_desc}</p>}
                      </div>
                      <p className="text-blue-200">${award.amount}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leaderboard Payouts */}
            {awardsSettings.leaderboardPayouts && awardsSettings.leaderboardPayouts.length > 0 && (
              <div className="bg-white/5 p-4 rounded-lg">
                <h3 className="text-white font-bold text-lg mb-3">📊 Leaderboard Payouts</h3>
                <div className="space-y-2">
                  {awardsSettings.leaderboardPayouts.map((payout) => (
                    <div key={payout.id} className="bg-white/5 p-3 rounded flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-yellow-400 font-bold">#{payout.placement}</span>
                          <p className="text-white font-semibold">{payout.name}</p>
                        </div>
                        {payout.description && <p className="text-blue-200 text-xs mb-1">{payout.description}</p>}
                        <p className="text-blue-300 text-sm capitalize">{payout.type}</p>
                        {payout.item_desc && <p className="text-blue-200 text-xs">{payout.item_desc}</p>}
                      </div>
                      <p className="text-green-300 font-bold text-lg">${payout.amount}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!awardsSettings.handicapSettings && awardsSettings.awards.length === 0 && awardsSettings.leaderboardPayouts.length === 0 && (
              <p className="text-blue-200 text-center">{t('superadmin.admins.noAwardsConfigured')}</p>
            )}

            <button
              onClick={() => setShowAwardsModal(false)}
              className="w-full mt-6 bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-bold transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditAdminModal && editAdminTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowEditAdminModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-6">✏️ Edit Admin</h2>
            {editAdminError && (
              <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{editAdminError}</p>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-blue-200 text-sm font-semibold mb-1">Name</label>
                <input
                  type="text"
                  value={editAdminForm.name}
                  onChange={e => setEditAdminForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-blue-200 text-sm font-semibold mb-1">Email</label>
                <input
                  type="email"
                  value={editAdminForm.email}
                  onChange={e => setEditAdminForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveAdminEdit}
                disabled={savingAdminEdit}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
              >
                {savingAdminEdit ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowEditAdminModal(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Password Modal for Admin */}
      {showAdminPwdModal && adminPwdTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAdminPwdModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-2">🔑 Set Password</h2>
            <p className="text-blue-200 text-sm mb-6">
              Setting a password for <strong className="text-white">{adminPwdTarget.name}</strong>{' '}
              <span className="text-blue-300">({adminPwdTarget.email})</span>
            </p>
            {adminPwdSuccess ? (
              <div className="text-center py-4">
                <p className="text-green-300 text-lg font-semibold mb-2">✅ Password set successfully!</p>
                <p className="text-blue-200 text-sm mb-6">An email was sent to {adminPwdTarget.email} with their new password.</p>
                <button
                  onClick={() => setShowAdminPwdModal(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold transition"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {adminPwdError && (
                  <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{adminPwdError}</p>
                )}
                <div className="mb-6">
                  <label className="block text-blue-200 text-sm font-semibold mb-1">New Password</label>
                  <input
                    type="text"
                    value={adminPwdValue}
                    onChange={e => setAdminPwdValue(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400"
                  />
                  <p className="text-blue-300 text-xs mt-1">The admin will be emailed their new password and told to change it.</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSetAdminPassword}
                    disabled={adminPwdLoading}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
                  >
                    {adminPwdLoading ? 'Setting...' : 'Set Password'}
                  </button>
                  <button
                    onClick={() => setShowAdminPwdModal(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterAdmins;
